import {
  flatMap,
  fromCondition,
  mapRight,
  pipe,
  returnsEffect,
  right,
  tapLeft,
  tapRight,
} from "@otto-ec/assets-core-utils/nano-effect";
import type { Unset } from "@otto-ec/global-resources/nexus";
import type { NavigationState } from "@otto-ec/global-resources/navigation";
import {
  navScope,
  statePropStack,
  type SheetNavState,
  statePropConfig,
  type SheetNavScope,
  statePropOnLoad,
  type SheetHistoryState,
} from "./scope";
import type { OcSheetV1Props } from "../SheetV1.types.g";
import { sheetCreate, sheetGoBack, sheetOpenById } from "../apis/methods.js";
import * as ACTIONS from "../event/event.action.defs";
import { applyProps } from "../utils/props";
import { findInstance, getInstance } from "../stack/stack.utils";
import { sheetTagName } from "../utils/consts";
import { makeSheetPropsWithoutOpen, parseSheetHashParamIntoIdOrProps } from "./parameter";

const log = /*           */ navScope.scope("fragment");

export const computeWhetherOpenOrClose = (
  currParam: string | null,
  prevParam: string | null,
  currStack: string[],
  prevStack: string[] | undefined,
): boolean | null => {
  /*                                                           */
  const stackGo = currStack.length - (prevStack?.length ?? 0);

  /*                                                */
  const shouldOpenNext = stackGo >= 0 && !!currParam;
  const shouldGoBack = stackGo < 0 || (!currParam && !!prevParam);

  if (shouldGoBack) return false; /*                                            */
  if (shouldOpenNext) return true; /*                             */
  return null; /*                                */
};

export const computeHowToOpenSheet = flatMap((stringOrProps: string | OcSheetV1Props) =>
  fromCondition(
    typeof stringOrProps === "string",
    stringOrProps as string,
    stringOrProps as OcSheetV1Props,
  ),
);

export const getActivationAction = (
  state: NavigationState<SheetHistoryState>,
): {
  action: typeof ACTIONS.SHEET_ACTION_PAGE_ENTRY_URL | typeof ACTIONS.SHEET_ACTION_USER_AGENT;
} => {
  if (state.get(statePropOnLoad)) {
    return { action: ACTIONS.SHEET_ACTION_PAGE_ENTRY_URL };
  }
  return { action: ACTIONS.SHEET_ACTION_USER_AGENT };
};

export const thenTryOpenSheetByIdFromHash = returnsEffect((curr: SheetNavState) => {
  return mapRight((sheetId: string) => {
    if (import.meta.env.DEV) log.info("sheet id found, try open with id", sheetId);
    const storedProps = curr.state.get(statePropConfig);

    let instance = findInstance(sheetId);
    if (instance) applyProps(instance, makeSheetPropsWithoutOpen(storedProps));

    /*                                                         */
    /*                                                      */
    /*                                               */
    const opened = sheetOpenById(sheetId, getActivationAction(curr.state));
    if (opened && !instance) {
      if (import.meta.env.DEV) log.info("instance", sheetId, "opened, apply props");
      instance = getInstance(sheetId);
      applyProps(instance, makeSheetPropsWithoutOpen(storedProps));
      return null;
    } else if (import.meta.env.DEV)
      log.info("could not open instance", sheetId, "from hash fragment");

    return sheetId;
  });
});

export const orTryOpenSheetByPropsFromHash = returnsEffect((curr: SheetNavState) => {
  return tapLeft((props: Partial<OcSheetV1Props> | null) => {
    if (import.meta.env.DEV) log.info("sheet props found, open with props", props);

    /*                                                                          */
    /*                                                                                 */
    const instance = sheetCreate(makeSheetPropsWithoutOpen(curr.state.get(statePropConfig), props));
    sheetOpenById(instance.id, getActivationAction(curr.state));
  });
});

export const ifFailsTryOpenSheetFromHistoryState = returnsEffect((curr: SheetNavState) =>
  tapRight((sheetId: string | null) => {
    if (!sheetId) {
      if (import.meta.env.DEV) log.info("no sheet id or props to open, skip processing");
      return;
    }

    const instance = findInstance(sheetId);
    const props = curr.state.get(statePropConfig);

    /*                             */
    if (!instance && props) {
      if (import.meta.env.DEV) log.info("no instance found, try to create one with props", props);

      sheetCreate(makeSheetPropsWithoutOpen(props));
      sheetOpenById(sheetId!, getActivationAction(curr.state));
    }
  }),
);

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const handleSheetScopeShange = (
  curr: SheetNavState,
  prev: SheetNavState | undefined,
): void => {
  if (curr.info === sheetTagName) {
    if (import.meta.env.DEV) log.info("skip hash change, already handled by stack handler");
    return;
  }

  const currParam = curr.hash.get(sheetTagName);
  const prevParam = prev?.hash.get(sheetTagName) ?? null;

  if (currParam === prevParam) {
    if (import.meta.env.DEV) log.info("no change in the hash parameter, skip update");
    return;
  }

  const currStack = curr.state.get(statePropStack) ?? [];
  const prevStack = prev?.state.get(statePropStack);
  if (import.meta.env.DEV) {
    log.info("Handle change to the scope state");
    log.debug("Hash change:", prevParam, "->", currParam);
    log.debug("Stack change:", prevStack, "->", currStack);
  }

  const openClose = computeWhetherOpenOrClose(currParam, prevParam, currStack, prevStack);
  if (openClose === null) {
    if (import.meta.env.DEV) log.info("no action needed, skip processing");
    return;
  }

  if (!openClose) {
    if (import.meta.env.DEV) log.info("revind sheet stack, go back to previous sheet");
    return void sheetGoBack({ action: ACTIONS.SHEET_ACTION_USER_AGENT });
  }

  if (import.meta.env.DEV) log.info("try to open next sheet with id or props", currParam);
  pipe(
    right(currParam!),
    /*                                                                                       */
    /*                                                                       */
    parseSheetHashParamIntoIdOrProps,

    /*                                                                             */
    /*                              */
    /*                                                                          */
    computeHowToOpenSheet,

    /*                                         */
    /*                                                                          */
    thenTryOpenSheetByIdFromHash(curr),

    /*                                                */
    /*                                                                          */
    orTryOpenSheetByPropsFromHash(curr),

    /*                                                     */
    /*                                                                                     */
    /*                                                                       */
    ifFailsTryOpenSheetFromHistoryState(curr),
  );
};

/**
 *
 *
 */
export const initQueryParams: (scope: SheetNavScope) => Unset = (scope) => {
  if (import.meta.env.DEV) navScope.info("init query params integration", history.length);
  return scope.current.sub(handleSheetScopeShange);
};
